import React, { useMemo, useState } from "react";
import {
    Badge,
    Button,
    FormLayout,
    HeaderActions,
    Heading,
    Input,
    LoadingProgress,
    Modal,
    Panel,
    Switch,
    Table,
    Text,
} from "@/ui";
import { Icon } from "@/components";
import { useMutation } from "@apollo/react-hooks";
import { MUTATION_UPDATE_ITERABLE } from "../../apollo/module-operations/app";
import _ from "lodash";
import { useForm, useMeta, useNotification, useValidator } from "@/hooks";
import SettingForm from "./SettingForm";
import { EVENT_TYPE } from "config/constant";
import {
    PlusIcon,
    RowActionsMenu,
    legacyColorToBadge,
    storeTableUiStyles,
} from "./shared";
import styles from "./GeneralsForm.module.scss";
import deliveryStyles from "./DeliverySchedules.module.scss";

const SaveIcon = () => <Icon icon="save" />;

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const IterableForm = ({ app, loading: pageLoading }) => {
    const notify = useNotification();
    const { user } = useMeta();
    const [showForm, setShowForm] = useState(false);
    const [isCreate, setIsCreate] = useState(true);
    const [rowSelect, setRowSelect] = useState(null);
    const [settings, setSettings] = useState([]);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDelete, setIsDelete] = useState(false);
    const [isloading, setIsLoading] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const { inputs, inputChange, setInputs } = useForm();

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "api_key",
                method: "isEmpty",
                validWhen: false,
                message: "This Api key is required.",
            },
        ],
        {
            ...inputs,
        },
    );

    if (!_.get(inputs, "id", null)) {
        setInputs(app);
    }

    const [mutate] = useMutation(MUTATION_UPDATE_ITERABLE, {
        refetchQueries: [],
        onCompleted: () => {
            handleCancel();
            setIsUpdating(false);
            setIsDelete(false);
            setShowDeleteConfirm(false);
            setInputs({ ...inputs, settings });
            setIsLoading(false);
            notify(
                `Setting Was ${
                    isCreate ? "Created" : !isDelete ? "Updated" : "Removed"
                } Successfully"`,
                "success",
            );
        },
        onError: () => {
            setIsUpdating(false);
            notify(
                `Error ${
                    isCreate ? "Updating" : !isDelete ? "Creating" : "Removing"
                }  The Setting, If The Error Persist Please Contact Support.`,
                "error",
            );
        },
    });

    const confirmDelete = () => {
        setIsUpdating(true);
        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: {
                    ...inputs,
                    settings: settings,
                },
            },
        });
    };

    const rawSettings = (inputs && inputs.settings) || [];
    const rows = useMemo(
        () =>
            rawSettings.map((setting, index) => ({
                ...setting,
                _index: index,
            })),
        [rawSettings],
    );

    const columns = useMemo(
        () => [
            {
                id: "actions",
                header: "",
                width: 52,
                align: "center",
                renderCell: row => (
                    <span
                        className={storeTableUiStyles.rowActions}
                        onClick={event => event.stopPropagation()}
                    >
                        <RowActionsMenu
                            disabled={isUpdating || showForm}
                            label={String(row.campaign_id ?? row._index + 1)}
                            onDelete={() => {
                                setIsDelete(true);
                                setIsCreate(false);
                                setSettings(
                                    _.filter(
                                        inputs.settings,
                                        (i, index) => index !== row._index,
                                    ),
                                );
                                setShowDeleteConfirm(true);
                            }}
                            onUpdate={() => {
                                setIsCreate(false);
                                setRowSelect(row._index);
                                setShowForm(true);
                            }}
                        />
                    </span>
                ),
            },
            {
                id: "campaign_id",
                header: "Campaign Number",
                minWidth: 180,
                renderCell: row => `${row.campaign_id || ""}`,
            },
            {
                id: "event_type",
                header: "Event type",
                minWidth: 180,
                renderCell: row =>
                    _.filter(EVENT_TYPE, e => e.value === row.event_type)[0]
                        ?.label || "",
            },
            {
                id: "event_name",
                header: "Event name",
                minWidth: 180,
                renderCell: row => `${row.event_name || ""}`,
            },
            {
                id: "schedule_send_email_hours",
                header: "Schedule send email hours",
                minWidth: 220,
                renderCell: row => (
                    <Badge
                        variant={legacyColorToBadge(
                            row.schedule_send_email_hours ? "success" : "error",
                        )}
                    >
                        {row.schedule_send_email_hours
                            ? `scheduled ${row.schedule_send_email_hours} hrs after`
                            : "Not scheduled"}
                    </Badge>
                ),
            },
        ],
        [isUpdating, showForm, inputs.settings],
    );

    const handlerUpdatingOrCreate = array => {
        setIsUpdating(true);
        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: {
                    ...inputs,
                    api_key: inputs.api_key,
                    settings: array,
                },
            },
        });
    };

    const handleCancel = () => {
        setIsUpdating(false);
        setShowForm(false);
    };

    const createOrUpdate = () => {
        if (isSubmited) {
            isSubmited(true);
        }

        if (isValid()) {
            setIsLoading(true);
            setSettings(inputs.settings);
            handlerUpdatingOrCreate(inputs.settings);
        }
    };

    return (
        <>
            <Modal
                description="Are You Sure You Want To Remove The Setting ?"
                footer={
                    <div className={deliveryStyles.modalActions}>
                        <Button
                            loading={isUpdating}
                            onClick={confirmDelete}
                            type="button"
                        >
                            Confirm
                        </Button>
                        <Button
                            disabled={isUpdating}
                            onClick={() => {
                                setShowDeleteConfirm(false);
                                setIsDelete(false);
                            }}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </div>
                }
                onOpenChange={open => {
                    if (!open) {
                        setShowDeleteConfirm(false);
                        setIsDelete(false);
                    }
                }}
                open={showDeleteConfirm}
                title="Remove setting"
            >
                {null}
            </Modal>

            <Panel
                className={styles.root}
                header={
                    <div className={styles.headerBar}>
                        <div className={styles.headerText}>
                            <Heading level={4}>Iterable</Heading>
                            <Text tone="secondary" variant="body-sm">
                                Iterable Information
                            </Text>
                        </div>
                        <HeaderActions
                            actions={[
                                {
                                    id: "add-setting",
                                    label: "Add Setting",
                                    icon: <PlusIcon />,
                                    variant: "secondary",
                                    disabled: isUpdating || showForm,
                                    onClick: () => {
                                        setIsCreate(true);
                                        setShowForm(true);
                                    },
                                },
                                {
                                    id: "update",
                                    label: "Update",
                                    icon: isloading ? undefined : <SaveIcon />,
                                    loading: isloading,
                                    disabled: isloading || showForm,
                                    onClick: createOrUpdate,
                                },
                            ]}
                            aria-label="Iterable actions"
                        />
                    </div>
                }
                showHeaderDivider
            >
                {user.loading || pageLoading ? (
                    <div className={styles.loading}>
                        <LoadingProgress
                            indeterminate
                            indicator="circular"
                            size="sm"
                            title="Loading"
                        />
                    </div>
                ) : (
                    <FormLayout>
                        <FormLayout.Section title="Information">
                            <Switch
                                checked={Boolean(inputs.used_for_subscription)}
                                label="Used for subscription"
                                name="used_for_subscription"
                                onChange={inputChange}
                            />
                            <FormLayout.Grid columns={2} maxWidth="twoThirds">
                                <Input
                                    error={fieldError(validator, "api_key")}
                                    fullWidth
                                    label="Api key"
                                    name="api_key"
                                    onChange={inputChange}
                                    placeholder="Api key"
                                    value={inputs.api_key || ""}
                                />
                            </FormLayout.Grid>
                        </FormLayout.Section>

                        {showForm ? (
                            <SettingForm
                                app={inputs}
                                handleCancel={handleCancel}
                                handlerUpdatingOrCreate={
                                    handlerUpdatingOrCreate
                                }
                                isCreate={isCreate}
                                isUpdating={isUpdating}
                                selectSettings={rowSelect}
                                setSettings={setSettings}
                            />
                        ) : null}

                        <FormLayout.Section title="Settings">
                            <Table
                                columns={columns}
                                getRowId={row => String(row._index)}
                                rows={rows}
                            />
                        </FormLayout.Section>
                    </FormLayout>
                )}
            </Panel>
        </>
    );
};

export default IterableForm;
